import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { driverPriceFor, savingVsMeter } from "./distance-pricing";
import { tariffFor, tariffPrice } from "./tariffs";
import { nearbyPlaces } from "./nearby-places";
import type { Quote } from "./pricing";
import { memo } from "./server-memo";


const ROUTE_SELECT =
  "id, customer_price, driver_price, pricing_model, ride_type, distance_km, duration_min, min_price, max_price, surcharge, discount, from_city:from_city_id(id,name,slug,kind), to_city:to_city_id(id,name,slug,kind)";

type CityRow = { id: string; name: string; slug: string; kind: string };

/** Alle actieve steden/luchthavens (publiek). */
export const listCities = createServerFn({ method: "GET" }).handler(async () => {
    return memo("cities", async () => {
  const { publicDb } = await import("./db.server");
  const { data, error } = await publicDb()
    .from("cities")
    .select("id, name, slug, kind, province")
    .order("name");
  if (error) throw new Error(error.message);
  return data ?? [];
    });
});

/** Actieve routes met klantprijs (publiek, nooit chauffeursprijs). */
export const listPublicRoutes = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ limit: z.number().int().min(1).max(500).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    return memo(`routes:${data.limit ?? 300}`, async () => {
    const { publicDb } = await import("./db.server");
    const { data: rows, error } = await publicDb()
      .from("routes")
      .select(
        "id, customer_price, ride_type, distance_km, duration_min, from_city:from_city_id(name,slug,kind), to_city:to_city_id(name,slug,kind)",
      )
      .limit(data.limit ?? 300);
    if (error) throw new Error(error.message);
    return (rows ?? [])
      .map((r) => ({
        ...r,
        // Alleen het vaste tarief uit de centrale tabel mag zichtbaar zijn.
        // Bestaat dat niet, dan toont de pagina geen bedrag maar een CTA.
        customer_price: tariffPrice(
          (r.from_city as unknown as CityRow)?.slug ?? "",
          (r.to_city as unknown as CityRow)?.slug ?? "",
        ),
      }))
      .sort((a, b) =>
      `${(a.from_city as unknown as CityRow)?.name}${(a.to_city as unknown as CityRow)?.name}`.localeCompare(
        `${(b.from_city as unknown as CityRow)?.name}${(b.to_city as unknown as CityRow)?.name}`,
      ),
    );
    });
  });

const quoteInput = z.object({
  fromSlug: z.string().min(1),
  toSlug: z.string().min(1),
});

async function buildQuote(fromSlug: string, toSlug: string): Promise<Quote> {
  // Serverzijde met verhoogde rechten: deze query leest ook de chauffeursprijs
  // en de prijsinstellingen. Die gegevens mogen niet publiek leesbaar zijn.
  const { adminDb } = await import("./db.server");
  const db = await adminDb();


  const { data: cities } = await db
    .from("cities")
    .select("id, name, slug, kind")
    .in("slug", [fromSlug, toSlug]);
  const fromCity = (cities ?? []).find((c) => c.slug === fromSlug) ?? null;
  const toCity = (cities ?? []).find((c) => c.slug === toSlug) ?? null;

  const empty: Quote = {
    kind: "quote",
    routeId: null,
    fromCity,
    toCity,
    fromLabel: fromCity?.name ?? fromSlug,
    toLabel: toCity?.name ?? toSlug,
    customerPrice: null,
    exact: false,
    driverPrice: null,
    rideType: "city",
    pricingModel: "quote",
    distanceKm: null,
    durationMin: null,
  };

  if (!fromCity || !toCity) {
    return { ...empty, message: "Deze plaats kennen wij nog niet. Vraag een prijs op maat aan." };
  }

  const { data: route } = await db
    .from("routes")
    .select(ROUTE_SELECT)
    .eq("from_city_id", fromCity.id)
    .eq("to_city_id", toCity.id)
    .maybeSingle();

  // Eén bron van waarheid: het vaste tarief van deze route. Dit is exact het
  // bedrag dat de pagina toont, de calculator berekent en de checkout afrekent.
  const tariff = tariffFor(fromCity.slug, toCity.slug);
  if (tariff) {
    return {
      kind: "fixed",
      routeId: route?.id ?? null,
      fromCity,
      toCity,
      fromLabel: fromCity.name,
      toLabel: toCity.name,
      customerPrice: tariff.price,
      exact: true,
      driverPrice: driverPriceFor(tariff.price),
      rideType: route?.ride_type ?? (fromCity.kind === "airport" || toCity.kind === "airport" ? "airport" : "city"),
      pricingModel: "tariff",
      distanceKm: tariff.km ?? (route?.distance_km == null ? null : Number(route.distance_km)),
      durationMin: route?.duration_min ?? null,
    };
  }

  // Geen vast tarief voor deze route: dan tonen wij geen bedrag. De klant
  // berekent zijn exacte vaste prijs met zijn eigen adressen. Een richtprijs
  // of vanaf-bedrag zou kunnen afwijken van de boekingsprijs en mag niet.
  return {
    ...empty,
    message: "Vul uw ophaaladres en bestemming in voor uw exacte vaste prijs.",
  };
}

/** Prijs voor een route (publiek). Chauffeursprijs wordt eruit gefilterd. */
export const getQuote = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => quoteInput.parse(input))
  .handler(async ({ data }) => {
    const quote = await buildQuote(data.fromSlug, data.toSlug);
    return { ...quote, driverPrice: null };
  });

/** SEO-routepagina: route + veelgestelde vragen + interne links. */
export const getRoutePage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => quoteInput.parse(input))
  .handler(async ({ data }) => {
    const quote = await buildQuote(data.fromSlug, data.toSlug);
    const { publicDb } = await import("./db.server");
    const db = publicDb();
    let related: { name: string; slug: string; price: number }[] = [];
    if (quote.fromCity) {
      const { data: rows } = await db
        .from("routes")
        .select("customer_price, distance_km, to_city:to_city_id(name,slug)")
        .eq("from_city_id", quote.fromCity.id)
        .limit(8);
      const fromSlug = quote.fromCity.slug;
      related = (rows ?? [])
        .map((r) => ({
          name: (r.to_city as unknown as CityRow)?.name,
          slug: (r.to_city as unknown as CityRow)?.slug,
          // Alleen routes met een vast tarief krijgen een bedrag te zien.
          price: tariffPrice(fromSlug, (r.to_city as unknown as CityRow)?.slug ?? ""),
        }))
        .filter(
          (r): r is { name: string; slug: string; price: number } =>
            Boolean(r.slug) && r.slug !== data.toSlug && r.price != null,
        );

    }
    return { quote: { ...quote, driverPrice: null }, related };
  });

/** SEO-stadspagina: stad + alle bestemmingen. */
export const getCityPage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const { publicDb } = await import("./db.server");
    const db = publicDb();
    const { data: city } = await db
      .from("cities")
      .select("id, name, slug, kind, province")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!city) return null;
    const { data: rows } = await db
      .from("routes")
      .select("customer_price, duration_min, distance_km, to_city:to_city_id(name,slug,kind)")
      .eq("from_city_id", city.id)
      .limit(100);
    const destinations = (rows ?? [])
      .map((r) => ({
        name: (r.to_city as unknown as CityRow)?.name,
        slug: (r.to_city as unknown as CityRow)?.slug,
        kind: (r.to_city as unknown as CityRow)?.kind,
        // Vast tarief uit de centrale tabel; zonder tarief geen bedrag.
        price: tariffPrice(city.slug as string, (r.to_city as unknown as CityRow)?.slug ?? ""),
        durationMin: r.duration_min,

        distanceKm: r.distance_km == null ? null : Number(r.distance_km),
      }))
      .filter((d) => d.slug && d.price != null)
      .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    // Plaatsen in de buurt op werkelijke afstand (coördinaten), niet op provincie.
    const candidates = nearbyPlaces(city.slug as string, { maxKm: 25, limit: 6 });
    let nearbyCities: { name: string; slug: string; km: number }[] = [];
    if (candidates.length) {
      const { data: existing } = await db
        .from("cities")
        .select("slug")
        .in(
          "slug",
          candidates.map((c) => c.slug),
        );
      const known = new Set((existing ?? []).map((c) => c.slug as string));
      const kept = candidates.filter((c) => known.has(c.slug));
      nearbyCities = kept.length >= 3 ? kept : [];
    }
    return { city, destinations, nearbyCities };

  });

/** Prijzenpagina: routes vanaf een stad met meterprijzen (max. 10). */
export const getCityPricing = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    return memo(`pricing:${data.slug}`, async () => {
    const { publicDb } = await import("./db.server");
    const db = publicDb();
    const { data: city } = await db
      .from("cities")
      .select("id, name, slug, kind, province")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!city) return null;

    const { data: rows } = await db
      .from("routes")
      .select("customer_price, duration_min, distance_km, to_city:to_city_id(name,slug,kind)")
      .eq("from_city_id", city.id)
      .not("distance_km", "is", null)
      .limit(100);

    const all = (rows ?? [])
      .map((r) => {
        const km = r.distance_km == null ? null : Number(r.distance_km);
        return {
          name: (r.to_city as unknown as CityRow)?.name,
          slug: (r.to_city as unknown as CityRow)?.slug,
          kind: (r.to_city as unknown as CityRow)?.kind,
          distanceKm: km,
          durationMin: r.duration_min,
          
          fixedPrice: tariffPrice(city.slug as string, (r.to_city as unknown as CityRow)?.slug ?? ""),
          saving: savingVsMeter(km, r.duration_min),
        };
      })
      .filter((d) => d.slug && d.distanceKm != null);

    // Toon de ritten waar onze vaste prijs het meeste scheelt t.o.v. een gewone taximeter.
    const interesting = all.filter((d) => (d.distanceKm ?? 0) >= 25 && d.saving >= 60);
    const destinations = (interesting.length >= 4 ? interesting : all)
      .sort((a, b) => b.saving - a.saving)
      .slice(0, 10)
      .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));


    // Plaatsen in de buurt op werkelijke afstand (coördinaten), niet op provincie.
    const candidates = nearbyPlaces(city.slug as string, { maxKm: 25, limit: 6 });
    let nearbyCities: { name: string; slug: string; km: number }[] = [];
    if (candidates.length) {
      const { data: existing } = await db
        .from("cities")
        .select("slug")
        .in(
          "slug",
          candidates.map((c) => c.slug),
        );
      const known = new Set((existing ?? []).map((c) => c.slug as string));
      const kept = candidates.filter((c) => known.has(c.slug));
      nearbyCities = kept.length >= 3 ? kept : [];
    }

    return { city, destinations, nearbyCities };
    });

  });

/** Slug-resolver voor /taxi-<van>-<naar> en /taxi-<stad>. */
export const resolveSeoSlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const raw = data.slug.toLowerCase();
    if (!raw.startsWith("taxi-")) return { type: "none" as const };
    const rest = raw.slice(5);
    const { publicDb } = await import("./db.server");
    const slugs = await memo("city-slugs", async () => {
      const { data: cities } = await publicDb().from("cities").select("slug").order("slug");
      return (cities ?? []).map((c) => c.slug as string);
    });
    if (slugs.includes(rest)) return { type: "city" as const, city: rest };
    for (const from of slugs) {
      if (!rest.startsWith(`${from}-`)) continue;
      const to = rest.slice(from.length + 1);
      if (slugs.includes(to)) return { type: "route" as const, from, to };
    }
    return { type: "none" as const };
  });
